// The spike's camera (gate A2.4), now on @rpg-unit/follow-camera (the third-person follow camera, cut from this file
// and src/valley/camera.ts; decision 0077). What stays here is the game's side: its values come from its content (the
// `camera-profiles` rows of @rpg-unit/camera-profile-presets and the `camera-rig` row of @rpg-unit/follow-camera,
// both loaded into the registry from the units), a setting falls back to the settings table's default, and the
// planted-defect switches register in the spike's one faults record. `mountCamera` drives any THREE camera after any
// target that speaks @rpg/camera's `CameraTarget`: the valley's hero (src/valley/camera.ts) and the camera lab's
// scripted target (`?scene=camera`, ./lab.ts). Look input is the input's `look` action.
//
// Clocks (decisions 0010, 0011): look input is read once per rendered frame in the runtime's input phase (real time,
// live through hit-stop); the rig updates in the late phase after the frame's sim steps, reading the target at the
// frame's alpha. Nothing here runs in the sim phase.
import type * as THREE from "three";
import { CAMERA_FAULTS, type CameraProfile, type CameraTarget, type RigCollision, type RigSettings } from "@rpg/camera";
import type { CameraProfilePreset } from "@rpg-unit/camera-profile-presets";
import { mountFollowCamera, rigSettings, type CameraRigPreset, type FollowCamera, type LookSource } from "@rpg-unit/follow-camera";
import type { Runtime } from "@rpg/runtime";
import type { FaultGroup } from "../faults";
import type { SpikeContent } from "../content";
import type { CameraProfileRow, CameraRigRow } from "../content/types.gen";

export { pointerLockOnPress, type LookSource } from "@rpg-unit/follow-camera";

/** The camera's planted defects by the spike's fault name (`?faults=` and `__rpg.faults`), each one of
 *  @rpg/camera's CAMERA_FAULTS. */
export const CAMERA_FAULT_KEYS = {
  "camera-equal-rates": "equalArmRates",
  "camera-stepped": "steppedWithSim",
  "camera-no-collision": "noCollision",
  "camera-thin-solid": "thinAsSolid",
  "camera-blend-frames": "blendCountsFrames",
} as const satisfies Record<string, keyof typeof CAMERA_FAULTS>;

/** The camera's fault group for the spike's one faults record; reads `?faults=` for its own names on creation. */
export function cameraFaultGroup(search = typeof location !== "undefined" ? location.search : ""): FaultGroup {
  const on = (new URLSearchParams(search).get("faults") ?? "").split(",");
  for (const [name, key] of Object.entries(CAMERA_FAULT_KEYS)) CAMERA_FAULTS[key] = on.includes(name);
  return {
    names: Object.keys(CAMERA_FAULT_KEYS),
    get: (n) => CAMERA_FAULTS[CAMERA_FAULT_KEYS[n as keyof typeof CAMERA_FAULT_KEYS]] === true,
    set: (n, v) => {
      CAMERA_FAULTS[CAMERA_FAULT_KEYS[n as keyof typeof CAMERA_FAULT_KEYS]] = v;
    },
  };
}

/** The camera's values from the content: the profiles by name, the rig settings, the rig row, the profile rows. */
export function cameraValues(content: SpikeContent): { profiles: Record<string, CameraProfile>; layers: Record<string, string>; settings: RigSettings; rig: CameraRigRow; rows: readonly CameraProfileRow[] } {
  const rows = content.registry.rows("camera-profiles") as readonly CameraProfileRow[];
  const rig = content.registry.get("camera-rig", "default") as CameraRigRow | undefined;
  if (!rig) throw new Error("camera: the camera-rig table has no 'default' row");
  const profiles: Record<string, CameraProfile> = {};
  const layers: Record<string, string> = {};
  for (const { id, layer, reason: _r, ...p } of rows) {
    profiles[id] = p;
    layers[id] = layer;
  }
  if (!profiles[rig.initial]) throw new Error(`camera: the starting profile "${rig.initial}" is not in camera-profiles`);
  return { profiles, layers, settings: rigSettings(rig as CameraRigPreset), rig, rows };
}

/** A mounted camera: the feature's. */
export type SpikeCamera = FollowCamera;

/**
 * Drive `camera` after `target`: look input in the input phase, the rig in the late phase (before anything that
 * draws the target, so both read the same alpha).
 */
export function mountCamera(o: {
  runtime: Runtime;
  camera: THREE.PerspectiveCamera;
  target: CameraTarget;
  content: SpikeContent;
  collision?: RigCollision | null;
  look?: LookSource;
  yaw?: number;
  /** A setting's value now (the A4 settings: `camera.sensitivity`, `camera.invertY`); the table's default without. */
  setting?: (id: string) => unknown;
}): SpikeCamera {
  const v = cameraValues(o.content);
  return mountFollowCamera({
    runtime: o.runtime,
    camera: o.camera,
    target: o.target,
    profiles: v.rows as readonly CameraProfilePreset[],
    rig: v.rig as CameraRigPreset,
    collision: o.collision ?? null,
    yaw: o.yaw ?? 0,
    ...(o.look ? { look: o.look } : {}),
    setting: (id) => o.setting?.(id) ?? o.content.registry.get("settings", id)?.default,
  });
}
